/**
 * The editor column (the prototype's `settingsEditor()` frame): phone back bar, the title row (icon,
 * category, record name, status label) and `<form id="settings-form">`.
 *
 * Design fix 7: a record's **Enabled** switch lives in the header, beside the status label — never in
 * the form body. Pass `enabled` to get both; pass only `status` for a label without a switch.
 */

import type { FormEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../icons/Icon.js';
import { Tool } from '../../ui/Tool.js';
import { ui } from '../../app/ui-store.js';
import { useSettingsLocation } from './context.js';

export interface EditorFrameProps {
  title: string;
  icon: string;
  /** The header status label, e.g. { label: 'Enabled', on: true } or a model's state. */
  status?: { label: string; on: boolean };
  /** The record's Enabled switch, drawn in the header next to the status label. */
  enabled?: { checked: boolean; onChange(checked: boolean): void; disabled?: boolean };
  children: ReactNode;
}

export function EditorFrame({ title, icon, status, enabled, children }: EditorFrameProps) {
  const location = useSettingsLocation();
  const navigate = useNavigate();
  const label = status ?? (enabled ? { label: enabled.checked ? 'Enabled' : 'Disabled', on: enabled.checked } : null);
  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void ui.get().draft?.save();
  };
  return (
    <main className="settings-editor">
      <div className="editor-mobile-back">
        <Tool icon="arrow-left" label="Back to items" onClick={() => navigate(`/settings/${location?.category ?? ''}`)} />
        <span>{location?.definition.name}</span>
      </div>
      <div className="editor-title">
        <span className="editor-title-icon">
          <Icon name={icon} />
        </span>
        <div>
          <small>{location?.definition.name}</small>
          <h1>{title}</h1>
        </div>
        {label ? (
          <span className="editor-header-status">
            <span className={label.on ? 'status-label enabled' : 'status-label'}>{label.label}</span>
            {enabled ? (
              <label className="switch-row editor-enabled-switch" title={enabled.checked ? 'Enabled' : 'Disabled'}>
                <input
                  type="checkbox"
                  name="enabled"
                  aria-label="Enabled"
                  checked={enabled.checked}
                  disabled={enabled.disabled}
                  onChange={(e) => enabled.onChange(e.target.checked)}
                />
                <i />
              </label>
            ) : null}
          </span>
        ) : null}
      </div>
      <form id="settings-form" onSubmit={onSubmit} noValidate={false}>
        {children}
        <input type="submit" className="hidden-submit" tabIndex={-1} aria-hidden="true" />
      </form>
    </main>
  );
}

/** The empty editor column ("Select an item"). */
export function EditorEmpty({ text = 'Select an item' }: { text?: string }) {
  return (
    <main className="settings-editor">
      <div className="empty">
        <h2>{text}</h2>
      </div>
    </main>
  );
}
